import React, { useEffect, useState } from "react";


const PaiementForm = ({
    paiement = null,
    modesPaiement = [],
    rendezVous = [],
    onSubmit,
    onCancel,
    loading: externalLoading = false
}) => {

    // =====================================================
    // STATE
    // =====================================================

    const [formData, setFormData] = useState({
        rendez_vous_id: "",
        mode_paiement_id: "",
        statut: "payé"
    });

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const isLoading = loading || externalLoading;


    // =====================================================
    // RENDEZ-VOUS DISPONIBLES
    //
    // Conditions :
    //
    // 1. Le RDV possède une finance
    // 2. Le RDV ne possède pas encore de paiement
    //
    // En modification :
    // on garde également le RDV du paiement actuel.
    // =====================================================

    const rendezVousAvecFinance = Array.isArray(rendezVous)

        ? rendezVous.filter((rdv) => {

            // -------------------------------------------------
            // Le rendez-vous doit avoir une finance
            // -------------------------------------------------

            if (!rdv.finance_id) {
                return false;
            }


            // -------------------------------------------------
            // Modification d'un paiement existant
            //
            // On garde le RDV actuellement associé
            // au paiement même s'il possède déjà paiement_id.
            // -------------------------------------------------

            if (
                paiement &&
                Number(rdv.id) ===
                Number(paiement.rendez_vous_id)
            ) {
                return true;
            }


            // -------------------------------------------------
            // Nouveau paiement :
            // le RDV ne doit pas déjà avoir un paiement
            // -------------------------------------------------

            return !rdv.paiement_id;

        })

        : [];


    // =====================================================
    // INITIALISATION
    // =====================================================

    useEffect(() => {

        if (paiement) {

            setFormData({

                rendez_vous_id:
                    paiement.rendez_vous_id
                        ? String(
                            paiement.rendez_vous_id
                        )
                        : "",

                mode_paiement_id:
                    paiement.mode_paiement_id
                        ? String(
                            paiement.mode_paiement_id
                        )
                        : "",

                statut:
                    paiement.statut || "payé"

            });

        } else {

            setFormData({

                rendez_vous_id: "",

                mode_paiement_id: "",

                statut: "payé"

            });

        }

        setError("");

    }, [paiement]);


    // =====================================================
    // CHANGEMENT
    // =====================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData((prev) => ({

            ...prev,

            [name]: value

        }));


        setError("");

    };


    // =====================================================
    // RENDEZ-VOUS SÉLECTIONNÉ
    // =====================================================

    const rdvSelectionne = Array.isArray(rendezVous)

        ? rendezVous.find(
            (rdv) =>
                Number(rdv.id) ===
                Number(formData.rendez_vous_id)
        )

        : null;


    // =====================================================
    // MODE DE PAIEMENT SÉLECTIONNÉ
    // =====================================================

    const modeSelectionne = Array.isArray(modesPaiement)

        ? modesPaiement.find(
            (mode) =>
                Number(mode.id) ===
                Number(formData.mode_paiement_id)
        )

        : null;


    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate = (date) => {

        if (!date) {
            return "-";
        }


        const d = new Date(date);


        if (Number.isNaN(d.getTime())) {

            return date;

        }


        return d.toLocaleDateString(
            "fr-FR"
        );

    };


    // =====================================================
    // FORMAT HEURE
    // =====================================================

    const formatHeure = (heure) => {

        if (!heure) {
            return "-";
        }


        return String(heure).substring(
            0,
            5
        );

    };


    // =====================================================
    // FORMAT MONTANT
    // =====================================================

    const formatMontant = (montant) => {

        if (
            montant === null ||
            montant === undefined ||
            montant === ""
        ) {

            return "0,00 CAD";

        }


        return Number(montant).toLocaleString(
            "fr-FR",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        ) + " CAD";

    };


    // =====================================================
    // MONTANT
    //
    // Le montant vient de la finance.
    // =====================================================

    const montantPaiement =

        paiement?.montant ??

        paiement?.prix_ttc ??

        rdvSelectionne?.prix_ttc ??

        rdvSelectionne?.montant ??

        null;


    // =====================================================
    // SUBMIT
    // =====================================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        setError("");


        // -------------------------------------------------
        // VALIDATION RDV
        // -------------------------------------------------

        if (!formData.rendez_vous_id) {

            setError(
                "Veuillez sélectionner un rendez-vous."
            );

            return;

        }


        // -------------------------------------------------
        // VALIDATION MODE
        // -------------------------------------------------

        if (!formData.mode_paiement_id) {

            setError(
                "Veuillez sélectionner un mode de paiement."
            );

            return;

        }


        // -------------------------------------------------
        // VALIDATION STATUT
        // -------------------------------------------------

        if (!formData.statut) {

            setError(
                "Veuillez sélectionner un statut."
            );

            return;

        }


        try {

            setLoading(true);


            const data = {

                rendez_vous_id:
                    Number(
                        formData.rendez_vous_id
                    ),

                mode_paiement_id:
                    Number(
                        formData.mode_paiement_id
                    ),

                statut:
                    formData.statut

            };


            console.log(
                "Paiement envoyé :",
                data
            );


            await onSubmit(data);


        } catch (err) {

            console.error(
                "Erreur paiement :",
                err
            );


            setError(

                err?.response?.data?.message ||

                err?.message ||

                "Erreur lors de l'enregistrement du paiement."

            );


        } finally {

            setLoading(false);

        }

    };


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div className="paiement-form-container">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="paiement-form-header">

                <div>

                    <h2>

                        {paiement

                            ? "Modifier le paiement"

                            : "Nouveau paiement"

                        }

                    </h2>


                    <p>

                        {paiement

                            ? "Modifier les informations du paiement"

                            : "Enregistrer un nouveau paiement"

                        }

                    </p>

                </div>

            </div>


            {/* =================================================
                ERREUR
            ================================================= */}

            {error && (

                <div className="alert alert-danger">

                    ⚠️ {error}

                </div>

            )}


            <form onSubmit={handleSubmit}>


                {/* =================================================
                    RENDEZ-VOUS
                ================================================= */}

                <div className="form-group">

                    <label htmlFor="rendez_vous_id">

                        Rendez-vous *

                    </label>


                    <select

                        id="rendez_vous_id"

                        name="rendez_vous_id"

                        value={
                            formData.rendez_vous_id
                        }

                        onChange={
                            handleChange
                        }

                        disabled={
                            isLoading
                        }

                        required

                    >

                        <option value="">

                            -- Sélectionner un rendez-vous --

                        </option>


                        {rendezVousAvecFinance.length === 0 ? (

                            <option
                                value=""
                                disabled
                            >

                                Aucun rendez-vous disponible

                            </option>

                        ) : (

                            rendezVousAvecFinance.map(
                                (rdv) => (

                                    <option
                                        key={rdv.id}
                                        value={rdv.id}
                                    >

                                        RDV #{rdv.id}

                                        {rdv.date_rdv &&
                                            ` - ${formatDate(
                                                rdv.date_rdv
                                            )}`
                                        }

                                        {rdv.heure_debut &&
                                            ` - ${formatHeure(
                                                rdv.heure_debut
                                            )}`
                                        }

                                        {rdv.client &&
                                            ` - ${rdv.client}`
                                        }

                                    </option>

                                )
                            )

                        )}

                    </select>

                </div>


                {/* =================================================
                    INFORMATIONS RDV
                ================================================= */}

                {rdvSelectionne && (

                    <div className="form-group">

                        <label>

                            Informations du rendez-vous

                        </label>


                        <div className="form-readonly">


                            <div>

                                <strong>
                                    👤 Client :
                                </strong>{" "}

                                {rdvSelectionne.client || "-"}

                            </div>


                            {rdvSelectionne.telephone && (

                                <div>

                                    <strong>
                                        📞 Téléphone :
                                    </strong>{" "}

                                    {rdvSelectionne.telephone}

                                </div>

                            )}


                            <div>

                                <strong>
                                    📅 Date :
                                </strong>{" "}

                                {formatDate(
                                    rdvSelectionne.date_rdv
                                )}

                            </div>


                            <div>

                                <strong>
                                    🕐 Heure :
                                </strong>{" "}

                                {formatHeure(
                                    rdvSelectionne.heure_debut
                                )}

                                {" - "}

                                {formatHeure(
                                    rdvSelectionne.heure_fin
                                )}

                            </div>


                            <div>

                                <strong>
                                    💰 Finance :
                                </strong>{" "}

                                {rdvSelectionne.finance_id
                                    ? "Disponible"
                                    : "Aucune finance"
                                }

                            </div>

                        </div>

                    </div>

                )}


                {/* =================================================
                    MONTANT
                ================================================= */}

                <div className="form-group">

                    <label>

                        Montant

                    </label>


                    <div className="montant-info">

                        {montantPaiement !== null

                            ? formatMontant(
                                montantPaiement
                            )

                            : "Montant calculé automatiquement"

                        }

                    </div>


                    <small>

                        Le montant est récupéré automatiquement
                        depuis la finance du rendez-vous.

                    </small>

                </div>


                {/* =================================================
                    MODE DE PAIEMENT
                ================================================= */}

                <div className="form-group">

                    <label htmlFor="mode_paiement_id">

                        Mode de paiement *

                    </label>


                    <select

                        id="mode_paiement_id"

                        name="mode_paiement_id"

                        value={
                            formData.mode_paiement_id
                        }

                        onChange={
                            handleChange
                        }

                        disabled={
                            isLoading
                        }

                        required

                    >

                        <option value="">

                            -- Sélectionner un mode de paiement --

                        </option>


                        {!Array.isArray(
                            modesPaiement
                        ) ||
                        modesPaiement.length === 0 ? (

                            <option
                                value=""
                                disabled
                            >

                                Aucun mode de paiement disponible

                            </option>

                        ) : (

                            modesPaiement.map(
                                (mode) => (

                                    <option
                                        key={mode.id}
                                        value={mode.id}
                                    >

                                        {mode.nom}

                                    </option>

                                )
                            )

                        )}

                    </select>


                    {modeSelectionne && (

                        <small>

                            Mode sélectionné :{" "}

                            <strong>

                                {modeSelectionne.nom}

                            </strong>

                        </small>

                    )}

                </div>


                {/* =================================================
                    STATUT
                ================================================= */}

                <div className="form-group">

                    <label htmlFor="statut">

                        Statut *

                    </label>


                    <select

                        id="statut"

                        name="statut"

                        value={
                            formData.statut
                        }

                        onChange={
                            handleChange
                        }

                        disabled={
                            isLoading
                        }

                        required

                    >

                        <option value="payé">
                            Payé
                        </option>

                        <option value="partiel">
                            Partiel
                        </option>

                        <option value="annulé">
                            Annulé
                        </option>

                    </select>

                </div>


                {/* =================================================
                    ACTIONS
                ================================================= */}

                <div className="form-actions">

                    <button

                        type="button"

                        onClick={
                            onCancel
                        }

                        disabled={
                            isLoading
                        }

                    >

                        Annuler

                    </button>


                    <button

                        type="submit"

                        disabled={
                            isLoading
                        }

                    >

                        {isLoading

                            ? "Enregistrement..."

                            : paiement

                                ? "Modifier"

                                : "Enregistrer"

                        }

                    </button>

                </div>

            </form>

        </div>
    );
};


export default PaiementForm;